import { ReactNode, useCallback } from 'react'
import { tileEntityByIdSelector } from '~/app/store/tileEntity'
import { useSelector } from 'react-redux'
import { colorSwatchSelector, designByIdSelector } from '~/app/store/design'
import { isPreviouslyOrderedTemplate } from '~/shared/sets/previouslyOrderedTemplate'
import { AnalyticsNames, ANALYTICS_EVENT_ACTIONS, ANALYTICS_LABEL, PREVIEW_TYPE } from 'shared/constants'
import { resolveInitialSwatchId } from 'shared/resolveInitialSwatchId'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { getBypassApproval } from '~/app/store/debug'
import { galleryImpressionIdSelector } from '~/app/store/analytics/reducer'
import { useGreetingDescription } from '~/app/features/Gallery/hooks/useGreetingDescription/useGreetingDescription'
import { useSocialMediaGallery } from '~/app/features/Gallery/hooks/useSocialMediaGallery'
import { designPersonalizationContextIdSelector, hasColorSelector } from '~/app/store/designPersonalization/selectors'
import { useColorSwatchSelection } from '~/app/features/Gallery/components/DesignTile/hooks/useColorSwatchSelection'
import { useNewTemplateAltText } from '~/app/features/Gallery/hooks/useNewTemplateAltText'
import { NUMBER_OF_IMAGES_WITH_HIGH_PRIORITY } from '~/app/features/Gallery/components/PreviewArea/constants'
import { BaseImageProps } from '~/app/features/Gallery/components/DesignTile/interface'
import { usePrefetchQuery } from '~/app/features/Gallery/components/PreviewArea/hooks/usePrefetchQuery'
import type { DesignAnalyticsObject } from 'shared/analytics/interface'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { GalleryDesignsCurrentColorSwatch } from 'shared/types/gallery/designs'
import type { State } from 'shared/types/gallery/state'

export type PropTypes = {
  tileEntityId: string
  children: (
    tileEntity: State.TileEntity,
    imageProps: BaseImageProps,
    handleColorSwatchChange: (newDesignId: string) => void,
    colorSwatchObjects: GalleryContentQuery.ColorSwatch[],
    currentColorSwatch: GalleryDesignsCurrentColorSwatch,
    currentDesignId: string,
    greetingOptions: string,
    greetingTitle: string,
    shouldShowGreetingOptions: boolean,
    shouldHideForSocialMedia: boolean,
    isPreviouslyOrdered: boolean,
    onMouseEnter?: () => void,
  ) => ReactNode | null
}

function createTileAnalyticsData(tileEntity: State.TileEntity): DesignAnalyticsObject {
  return {
    tileEntity,
    position: tileEntity.position,
    location: AnalyticsNames.DesignTile,
  }
}

export const GalleryTileEntityProvider = (props: PropTypes) => {
  const { tileEntityId, children } = props
  const tileEntityById = useSelector(tileEntityByIdSelector)
  const selectColorSwatch = useSelector(colorSwatchSelector)
  const analytics = useAnalytics()
  const designById = useSelector(designByIdSelector)
  const personalizationHasColor = useSelector(hasColorSelector)
  const tileEntity = tileEntityById(tileEntityId)
  const { currentDesignId, selectSwatch } = useColorSwatchSelection({
    colorSwatchIds: tileEntity.colorSwatches,
    designById,
    personalizationHasColor,
    fallbackSwatchId: resolveInitialSwatchId(tileEntity),
  })
  const shouldHideForSocialMedia = useSocialMediaGallery()
  const dpcId = useSelector(designPersonalizationContextIdSelector)
  const isPreviouslyOrdered = isPreviouslyOrderedTemplate(tileEntity.rankingSet)

  const { greetingTitle, greetingOptions, shouldShowGreetingOptions } = useGreetingDescription(tileEntity)

  const currentColorSwatch = selectColorSwatch(currentDesignId, tileEntityId, PREVIEW_TYPE.TILE)

  const previewAlt = useNewTemplateAltText(tileEntity, currentColorSwatch.colorSwatches)
  const onMouseEnter = usePrefetchQuery(tileEntity)
  const bypassedApproval = useSelector(getBypassApproval)
  const impressionId = useSelector((state: State.GlobalState) => galleryImpressionIdSelector(state, tileEntityId))

  const { previewsUrls } = currentColorSwatch.renderablePreviews

  const imageProps: BaseImageProps = {
    alt: previewAlt,
    previewsUrls,
    bypassedApproval,
  }

  if (tileEntity.position < NUMBER_OF_IMAGES_WITH_HIGH_PRIORITY) {
    imageProps.fetchpriority = 'high'
  }

  const handleColorSwatchChange = useCallback(
    (newDesignId: string): void => {
      const newSwatch = designById(newDesignId)
      const color = newSwatch.color?.split('#')[1]
      const analyticsData = createTileAnalyticsData(tileEntity)
      const colorSwatchPersonalizedDetail = dpcId ? 'Tile color selection with personalized design' : ''

      analytics.trackEvent({
        action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
        eventLabel: ANALYTICS_LABEL.SELECT_COLOR_SWATCH,
        eventDetail: colorSwatchPersonalizedDetail,
        ...analytics.buildDesignEngagement({
          engagementAction: ANALYTICS_LABEL.SELECT_COLOR_SWATCH,
          selectedDesign: newDesignId,
          color,
          tileEntity: analyticsData.tileEntity,
          colorSwatchObjects: currentColorSwatch.colorSwatches,
          impressionId,
          location: analyticsData.location,
          position: analyticsData.tileEntity.position,
        }),
      })

      selectSwatch(newSwatch)
    },
    [designById, tileEntity, dpcId, analytics, currentColorSwatch.colorSwatches, impressionId, selectSwatch],
  )

  return children(
    tileEntity,
    imageProps,
    handleColorSwatchChange,
    currentColorSwatch.colorSwatches,
    currentColorSwatch,
    currentDesignId,
    greetingOptions,
    greetingTitle,
    shouldShowGreetingOptions,
    shouldHideForSocialMedia,
    isPreviouslyOrdered,
    onMouseEnter,
  )
}
